import { useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useMatch } from 'react-router-dom';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  useTranslationRef,
} from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { autheliaAuthApiRef } from '../shared/auth/autheliaAuthApiRef';
import type { ArgoApplication } from '../projectEnvironments/argoApp';
import { BRANCH_PAGE_PATH } from '../projectEnvironments/branchRouteRef';
import {
  applicationsByProject,
  loadAllApplications,
  onAllApplications,
  onProjectApplications,
  type ApplicationsRequest,
} from '../projectEnvironments/projectApplications';
import {
  ensureProjectListed,
  loadProjectEntries,
  onProjectEntries,
  type ProjectEntry,
} from '../projectEnvironments/projectList';
import { onBranchReadsForgotten } from '../projectEnvironments/branchReadsSignal';
import { useOnDocumentVisible } from '../projectEnvironments/visiblePolling';
import { projectEnvironmentsTranslationRef } from '../projectEnvironments/translation';

export interface ProjectNavReads {
  projects: ProjectEntry[];
  projectsLoading: boolean;
  /**
   * Every project's Applications, from the ONE list call the home page makes
   * too (`projectApplications.ts`). Undefined until that call has succeeded once.
   */
  appsByProject: Map<string, ArgoApplication[]> | undefined;
  setAppsByProject: Dispatch<
    SetStateAction<Map<string, ArgoApplication[]> | undefined>
  >;
  branchesLoading: boolean;
  /** The request of the ArgoCD list, for the reads made by the caller. */
  appsRequest: () => ApplicationsRequest;
}

/**
 * What the sidebar's projects block reads — the catalog's Odoo projects and
 * the ArgoCD environment Applications — and keeps current, shared by its
 * expanded (ProjectsNav.tsx) and compact (CompactProjectsNav.tsx) forms.
 *
 * `appsKey`: undefined, the Applications are not read at all; a value, they
 * are, once; a NEW value reads again if the previous read failed (the
 * expanded block keys it by the open project, so opening another one
 * retries).
 */
export function useProjectNavReads(
  appsKey: string | undefined,
): ProjectNavReads {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const catalogApi = useApi(catalogApiRef);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const autheliaAuth = useApi(autheliaAuthApiRef);
  const branchMatch = useMatch(BRANCH_PAGE_PATH);

  const [projects, setProjects] = useState<ProjectEntry[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [appsByProject, setAppsByProject] =
    useState<Map<string, ArgoApplication[]>>();
  const [branchesLoading, setBranchesLoading] = useState(false);

  // The request of the ArgoCD list, for the reads made outside the effect
  // below: a branch created or deleted, a return to the tab.
  const appsRequest = (): ApplicationsRequest => ({
    fetchApi,
    discoveryApi,
    autheliaAuth,
    noIdentityMessage: t('error.noIdentity'),
    notJsonMessage: (body: string) => t('error.notJson', { body }),
  });

  useEffect(() => {
    let active = true;

    const loadProjects = async () => {
      try {
        // Shared with the home page (`projectList.ts`): the same filter as
        // ever — annotation, label and owner, see projectEntity.ts.
        const found = await loadProjectEntries(catalogApi);

        if (active) {
          setProjects(found);
        }
      } catch {
        // Swallowed on purpose. Before sign-in every backend call fails, and a
        // navigation bar is not the place to report it: the block simply does
        // not appear. Errors from the catalog and from ArgoCD keep being shown
        // where they can be acted upon — on the entity card and on the branch
        // page.
        if (active) {
          setProjects([]);
        }
      } finally {
        if (active) {
          setProjectsLoading(false);
        }
      }
    };

    void loadProjects();

    return () => {
      active = false;
    };
  }, [catalogApi]);

  useEffect(() => {
    // Branches are read when asked for and never before, so a page load
    // without an open project costs no ArgoCD request at all.
    if (!appsKey) {
      return undefined;
    }

    // One list call for every project (`projectApplications.ts`), kept once it
    // has succeeded: expanding another project, or collapsing and
    // re-expanding, costs nothing. A *failed* read is deliberately not kept,
    // so the next expand retries it. The call is shared with the home page,
    // which has usually made it already.
    if (appsByProject) {
      return undefined;
    }

    let active = true;

    const loadBranches = async () => {
      setBranchesLoading(true);

      try {
        const apps = await loadAllApplications({
          fetchApi,
          discoveryApi,
          autheliaAuth,
          noIdentityMessage: t('error.noIdentity'),
          notJsonMessage: (body: string) => t('error.notJson', { body }),
        });

        if (active) {
          setAppsByProject(applicationsByProject(apps));
        }
      } catch {
        // Swallowed for the reason given on the catalog read above: nothing is
        // rendered under the project, and the card and the branch page remain
        // where an ArgoCD failure is reported.
      } finally {
        if (active) {
          setBranchesLoading(false);
        }
      }
    };

    void loadBranches();

    return () => {
      active = false;
    };
  }, [appsKey, appsByProject, fetchApi, discoveryApi, autheliaAuth, t]);

  // FOLLOWING THE OTHER SCREENS' READS, at no request of its own
  // (`sharedLoad.ts`): the projects and the environments the home page, a
  // poll or a Refresh elsewhere just read replace the sidebar's; one
  // project's Applications, as the environments card, the branch page and
  // its « being prepared » poll read them, replace that project's — so a
  // branch appearing or leaving there shows here too.
  useEffect(() => {
    const unsubscribeProjects = onProjectEntries(found => {
      setProjects(found);
      setProjectsLoading(false);
    });
    // Also ends the branches' loading state: a list read elsewhere can land
    // while the sidebar's own first read is still in flight, and the load
    // effect, re-run by the new `appsByProject`, then drops that read's
    // `finally` — which would leave the spinner on until a remount.
    const unsubscribeApps = onAllApplications(apps => {
      setAppsByProject(applicationsByProject(apps));
      setBranchesLoading(false);
    });
    const unsubscribeProject = onProjectApplications(
      fetchApi,
      (projectId, apps) =>
        // Only over a list already read: alone, one project's answer would
        // show every other project without environment.
        setAppsByProject(current => {
          if (!current) {
            return current;
          }
          const next = new Map(current);
          next.set(projectId, apps);
          return next;
        }),
    );
    return () => {
      unsubscribeProjects();
      unsubscribeApps();
      unsubscribeProject();
    };
  }, [fetchApi]);

  // Whether the list of environments is shown at all.
  const appsShown = Boolean(appsKey);
  const latestAppsShown = useRef(appsShown);
  latestAppsShown.current = appsShown;
  const latestAppsRequest = useRef(appsRequest);
  latestAppsRequest.current = appsRequest;

  // A branch created or deleted from this app (`forgetBranchReads`): the
  // list of environments again, ONE request — it was just forgotten — whose
  // answer reaches the subscription above. The Application of a new branch
  // takes up to three minutes to appear (the ApplicationSet's requeue): its
  // page's « being prepared » poll then brings it here. The figures of
  // « Éphémères · N/max » are read again by `useBranchMeta` itself.
  useEffect(
    () =>
      onBranchReadsForgotten(fetchApi, () => {
        if (!latestAppsShown.current) {
          return;
        }
        loadAllApplications(latestAppsRequest.current()).catch(() => {
          // Swallowed for the reason given on the reads above.
        });
      }),
    [fetchApi],
  );

  // BACK TO THE TAB: both lists again when older than their 30 s share —
  // a plain shared load, which reuses a recent answer and asks once
  // otherwise. The home page makes the same two loads on the same event
  // (`useHomeData.ts`: it needs the environments even when no project is
  // open here): whichever comes first asks, the other shares its request.
  // The branch page follows through its subscriptions.
  useOnDocumentVisible(() => {
    loadProjectEntries(catalogApi).catch(() => {
      // Swallowed for the reason given on the reads above.
    });
    if (latestAppsShown.current) {
      loadAllApplications(latestAppsRequest.current()).catch(() => {
        // Same.
      });
    }
  });

  // A branch page of a project the list does not have — one created after
  // the list was read: read once more (`ensureProjectListed`).
  const routeProject = branchMatch?.params.project;
  const routeProjectUnknown =
    !projectsLoading &&
    routeProject !== undefined &&
    !projects.some(project => project.projectId === routeProject);
  useEffect(() => {
    if (routeProjectUnknown && routeProject) {
      ensureProjectListed(catalogApi, routeProject).catch(() => {
        // Swallowed for the reason given on the reads above.
      });
    }
  }, [routeProjectUnknown, routeProject, catalogApi]);

  return {
    projects,
    projectsLoading,
    appsByProject,
    setAppsByProject,
    branchesLoading,
    appsRequest,
  };
}
